import apiClient from "../clients/apiClient";
import { ApiResponse } from "../schemas/apiResponseSchema";

export interface TransactionSetupResponse {
    no: number;
    menuitem?: string | null;
    type?: string | null;
    number?: string | null;
    reftype?: string | null;
    refqtychk?: boolean | number | null;
    desc?: string | null;
    hotra?: boolean | number | null;
    sourcetra?: boolean | number | null;
    desttra?: boolean | number | null;
    customer?: boolean | number | null;
    assistant?: boolean | number | null;
    payment?: boolean | number | null;
    supplier?: boolean | number | null;
    supitems?: boolean | number | null;
    reorderchk?: boolean | number | null;
    autolist?: boolean | number | null;
    ref1?: string | null;
    ref2?: string | null;
    stockadddeduct?: boolean | number | null;
    pricemode?: boolean | number | null;
    showprice?: boolean | number | null;
    linedisper?: boolean | number | null;
    linedisamt?: boolean | number | null;
    showcase?: boolean | number | null;
    caseqty?: boolean | number | null;
    unitqty?: boolean | number | null;
    freeqty?: boolean | number | null;
    chksih?: boolean | number | null;
    addition?: boolean | number | null;
    deduction?: boolean | number | null;
    netdisper?: boolean | number | null;
    netdisamt?: boolean | number | null;
    pricechange?: boolean | number | null;
    updateitemprice?: boolean | number | null;
    tempsave?: boolean | number | null;
    supbalance?: boolean | number | null;
    cusbalance?: boolean | number | null;
    reportname?: string | null;
    headertable?: string | null;
    detailtable?: string | null;
    paytable?: string | null;
    calavg?: boolean | number | null;
    barcodescan?: boolean | number | null;
    setuplocchk?: boolean | number | null;
    refheadertable?: string | null;
    refdetailtable?: string | null;
    fromlocsetup?: boolean | number | null;
    tolocsetup?: boolean | number | null;
    tax?: boolean | number | null;
    saveinv?: boolean | number | null;
    chkinvamt?: boolean | number | null;
    menukey?: string | null;
    menukeyname?: string | null;
    createitem?: boolean | number | null;
    showsih?: boolean | number | null;
    chkrefloc_toloc?: boolean | number | null;
    allowminus?: boolean | number | null;
    stocklocation?: string | null;
}

export interface DocumentCreateRequest {
    setup_no: number;
    header: Record<string, any>;
    details: Record<string, any>[];
    save_mode?: "temporary" | "final";
}

export interface StageLineRequest {
    setup_no: number;
    detail: Record<string, any>;
    line_no: number;
    temp_no?: string;
}

/**
 * Field mapping from UI field names to database column names
 */
export const headerFieldMapping: Record<string, string> = {
    docNo: "docNo",
    date: "trhed_date",
    time: "trhed_time",
    supplier: "trhed_supcode",
    from: "trhed_fromloc",
    customer: "trhed_memcode",
    to: "trhed_toloc",
    assistant: "trhed_repcode",
    transactionMode: "trhed_type",
    refTrnNo: "trhed_refno",
    validityDate: "trhed_validuntil",
    priceMode: "trhed_pricemode",
    paymentMode: "trhed_paymode",
    reorderBased: "trhed_process",
    gross: "trhed_invamt",
    netDiscount: "trhed_netdiscper",
    netDiscountAmount: "trhed_netdiscamt",
    addition: "trhed_additions",
    deduction: "trhed_deductions",
    due: "trhed_dueamount",
    tax: "trhed_taxamt",
    net: "trhed_netamount",
};

export const detailFieldMapping: Record<string, string> = {
    productCode: "trdet_procode",
    stockCode: "trdet_stockcode",
    description: "trdet_prodesc",
    group: "trdet_group",
    unit: "trdet_unit",
    caseSize: "trdet_casesize",
    cost: "trdet_cprice",
    selling: "trdet_sprice",
    avgCost: "trdet_aprice",
    sih: "trdet_sih",
    caseQty: "trdet_caseqty",
    balanceQty: "trdet_balqty",
    unitQty: "trdet_unitqty",
    freeQty: "trdet_freeqty",
    expiryDate: "trdet_expdate",
    discountPer: "trdet_disper",
    discountAmount: "trdet_disamt",
    tax: "trdet_taxamt",
    amount: "trdet_amount",
    remarks: "trdet_remarks",
    lotCode: "trdet_lot",
};

/**
 * Map header fields to their corresponding pos_trasetup flag names
 */
export const headerFieldFlagMapping: Record<string, keyof TransactionSetupResponse> = {
    supplier: "supplier",
    from: "sourcetra",
    customer: "customer",
    to: "desttra",
    assistant: "assistant",
    transactionMode: "stockadddeduct",
    refTrnNo: "refqtychk",
    validityDate: "createitem",
    priceMode: "pricemode",
    paymentMode: "payment",
    reorderBased: "autolist",
};

/**
 * Map grid columns to their corresponding pos_trasetup flag names
 */
export const gridFieldFlagMapping: Record<string, keyof TransactionSetupResponse> = {
    caseSize: "showcase",
    cost: "pricemode",
    selling: "showprice",
    avgCost: "calavg",
    sih: "showsih",
    caseQty: "caseqty",
    unitQty: "unitqty",
    freeQty: "freeqty",
    expiryDate: "chksih",
    discountPer: "linedisper",
    discountAmount: "linedisamt",
    tax: "tax",
    remarks: "tempsave",
};

/**
 * Map footer fields to their corresponding pos_trasetup flag names
 */
export const footerFieldFlagMapping: Record<string, keyof TransactionSetupResponse | null> = {
    gross: null, // Always show
    netDiscount: "netdisper",
    netDiscountAmount: "netdisamt",
    discountAmount: "linedisamt",
    addition: "addition",
    deduction: "deduction",
    tax: "tax",
    net: null, // Always show
};

/**
 * UI-only fields that should not be sent to the database
 */
const UI_ONLY_FIELDS = new Set([
    'runningNo', // Used for selecting from recent documents; not a database column
]);

/**
 * Convert UI field names to database column names
 */
export function mapHeaderFieldsToDb(headerData: Record<string, any>): Record<string, any> {
    const dbData: Record<string, any> = {};
    
    for (const [uiKey, value] of Object.entries(headerData)) {
        // Skip UI-only fields that don't map to database columns
        if (UI_ONLY_FIELDS.has(uiKey)) {
            continue;
        }
        
        const dbKey = headerFieldMapping[uiKey] || uiKey;
        dbData[dbKey] = value;
    }
    
    return dbData;
}

/**
 * Convert detail line UI field names to database column names
 */
export function mapDetailFieldsToDb(detail: Record<string, any>, lineNo: number): Record<string, any> {
    const dbData: Record<string, any> = {
        trdet_lineno: lineNo,
    };
    
    for (const [uiKey, value] of Object.entries(detail)) {
        const dbKey = detailFieldMapping[uiKey] || uiKey;
        dbData[dbKey] = value;
    }
    
    return dbData;
}

export const getTransactionSetups = async (): Promise<TransactionSetupResponse[]> => {
    try {
        const response: ApiResponse<TransactionSetupResponse[]> = (
            await apiClient.get("/transactions/setups")
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to fetch transaction setups");
    }
};

export const getTransactionSetup = async (setupNo: number): Promise<TransactionSetupResponse> => {
    try {
        const response: ApiResponse<TransactionSetupResponse> = (
            await apiClient.get(`/transactions/${setupNo}/setup`)
        ).data;

        if (!response.data) {
            throw new Error(`Transaction setup ${setupNo} not found`);
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error(`Failed to fetch transaction setup ${setupNo}`);
    }
};

export const createDocument = async (request: DocumentCreateRequest): Promise<Record<string, any>> => {
    try {
        // Map UI field names to database column names
        const dbHeader = mapHeaderFieldsToDb(request.header);
        const dbDetails = request.details.map((detail, index) => 
            mapDetailFieldsToDb(detail, index + 1)
        );
        
        const response: ApiResponse<Record<string, any>> = (
            await apiClient.post(`/transactions/${request.setup_no}/document`, {
                header: dbHeader,
                details: dbDetails,
                save_mode: request.save_mode || "final",
            })
        ).data;

        if (!response.data) {
            throw new Error("Document creation failed");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to create transaction document");
    }
};

export const stageTransactionLine = async (request: StageLineRequest): Promise<Record<string, any>> => {
    try {
        const mappedDetail = mapDetailFieldsToDb(request.detail, request.line_no);
        const response: ApiResponse<Record<string, any>> = (
            await apiClient.post(`/transactions/${request.setup_no}/stage-line`, {
                detail: mappedDetail,
                line_no: request.line_no,
                temp_no: request.temp_no,
            })
        ).data;

        if (!response.data) {
            throw new Error("Failed to stage line");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to stage temporary line");
    }
};

export const getDocumentHeader = async (
    setupNo: number,
    docNo: string
): Promise<Record<string, any>> => {
    try {
        const response: ApiResponse<Record<string, any>> = (
            await apiClient.get(`/transactions/${setupNo}/header/${docNo}`)
        ).data;

        if (!response.data) {
            throw new Error(`Document ${docNo} not found`);
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to fetch document header");
    }
};

export const getDocumentDetails = async (
    setupNo: number,
    docNo: string
): Promise<Record<string, any>[]> => {
    try {
        const response: ApiResponse<Record<string, any>[]> = (
            await apiClient.get(`/transactions/${setupNo}/details/${docNo}`)
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to fetch document details");
    }
};

export const getRecentDocuments = async (
    setupNo: number,
    limit: number = 10
): Promise<Record<string, any>[]> => {
    try {
        const response: ApiResponse<Record<string, any>[]> = (
            await apiClient.get(`/transactions/${setupNo}/recent?limit=${limit}`)
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to fetch recent documents");
    }
};

export interface TransactionDocumentSearchParams {
    q?: string;
    date_from?: string;
    date_to?: string;
    status?: "all" | "temporary" | "processed";
    amount_min?: number;
    amount_max?: number;
    limit?: number;
    offset?: number;
}

export const searchTransactionDocuments = async (
    setupNo: number,
    params: TransactionDocumentSearchParams = {},
): Promise<Record<string, any>[]> => {
    try {
        const response: ApiResponse<Record<string, any>[]> = (
            await apiClient.get(`/transactions/${setupNo}/documents/search`, { params })
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to search transaction documents");
    }
};

/**
 * Fetch item lot data including product details
 */
export const getItemLot = async (
    itemCode: string,
    lotCode?: string,
    location?: string,
): Promise<Record<string, any>> => {
    try {
        const params: Record<string, string> = {};
        if (lotCode) params.lot_code = lotCode;
        if (location && location.trim()) params.location = location.trim();
        const response: ApiResponse<Record<string, any>> = (
            await apiClient.get(`/item-lots/${encodeURIComponent(itemCode)}`, {
                params: Object.keys(params).length ? params : undefined,
            })
        ).data;

        return response.data || {};
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to fetch item lot data");
    }
};

/**
 * Search item lots by code, description, or barcode
 */
export const searchItemLots = async (
    searchText: string,
    limit?: number,
    location?: string,
): Promise<Record<string, any>[]> => {
    try {
        const params: Record<string, string | number> = {};
        if (typeof limit === "number") params.limit = limit;
        if (location && location.trim()) params.location = location.trim();
        const response: ApiResponse<Record<string, any>[]> = (
            await apiClient.get(`/item-lots/search/${encodeURIComponent(searchText)}`, {
                params: Object.keys(params).length ? params : undefined,
            })
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to search item lots");
    }
};

/**
 * Get case quantity for an item
 */
export const getItemCaseQty = async (itemCode: string): Promise<number> => {
    try {
        const response: ApiResponse<Record<string, any>> = (
            await apiClient.get(`/item-lots/${encodeURIComponent(itemCode)}/case-qty`)
        ).data;

        return response.data?.caseQty || 0;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to fetch case quantity");
    }
};

/**
 * Get all temporary documents from local session (for UI refresh restoration)
 */
export const getAllTempDocuments = async (transactionType?: string | null): Promise<Record<string, any>[]> => {
    try {
        const response: ApiResponse<Record<string, any>[]> = (
            await apiClient.get(`/transactions/temp-documents/all`, {
                params: transactionType ? { transaction_type: transactionType } : undefined,
            })
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to fetch temporary documents");
    }
};

/**
 * Get all temporary lines for a specific document (for UI refresh restoration)
 */
export const getTempLinesForDocument = async (tempNo: string, transactionType?: string | null): Promise<Record<string, any>[]> => {
    try {
        const response: ApiResponse<Record<string, any>[]> = (
            await apiClient.get(`/transactions/temp-lines/${encodeURIComponent(tempNo)}`, {
                params: transactionType ? { transaction_type: transactionType } : undefined,
            })
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to fetch temporary lines");
    }
};

/**
 * Calculate line discount and totals
 */
export interface DiscountCalculationRequest {
    quantity: number;
    unit_price: number;
    discount_percentage: number;
    discount_amount: number;
}

export interface DiscountCalculationResult {
    discount_amount: number;
    line_amount: number;
    discount_percentage: number;
    gross_amount: number;
}

export const calculateLineDiscount = async (request: DiscountCalculationRequest): Promise<DiscountCalculationResult> => {
    try {
        const response: ApiResponse<DiscountCalculationResult> = (
            await apiClient.post(`/transactions/calculate-discount`, request)
        ).data;

        return response.data || {
            discount_amount: 0,
            line_amount: 0,
            discount_percentage: 0,
            gross_amount: 0,
        };
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to calculate discount");
    }
};

/**
 * Report generation interfaces
 */
export interface GenerateTransactionReportRequest {
    setup_no: number;
    doc_no: string;
    report_type?: string;
    format?: "pdf" | "html";
}

export interface ReportPreviewData {
    header: Record<string, any>;
    details: Record<string, any>[];
    doc_no: string;
    setup_no: number;
}

/**
 * Get available transaction report types
 */
export const getAvailableReportTypes = async (): Promise<Array<{ type: string; label: string; description: string }>> => {
    try {
        const response: ApiResponse<Array<{ type: string; label: string; description: string }>> = (
            await apiClient.get("/transaction-reports/available-types")
        ).data;

        return response.data || [];
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to fetch available report types");
    }
};

/**
 * Generate and download transaction report as PDF
 */
export const generateTransactionReportPdf = async (
    setupNo: number,
    docNo: string,
    reportType: string = ""
): Promise<void> => {
    try {
        const normalizedReportType = reportType.trim();
        const requestBody: Record<string, any> = {
            setup_no: setupNo,
            doc_no: docNo,
            format: "pdf",
        };

        if (normalizedReportType) {
            requestBody.report_type = normalizedReportType;
        }

        const response = await apiClient.post(
            "/transaction-reports/generate",
            requestBody,
            {
                responseType: "arraybuffer",
            }
        );

        // Create blob from response
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);

        // Create download link
        const link = document.createElement("a");
        link.href = url;
        link.download = `${docNo}_${normalizedReportType || "transaction"}_${new Date().toISOString().split("T")[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to generate report");
    }
};

/**
 * Get transaction report preview (header and detail lines)
 */
export const getTransactionReportPreview = async (
    setupNo: number,
    docNo: string
): Promise<ReportPreviewData> => {
    try {
        const response: ApiResponse<ReportPreviewData> = (
            await apiClient.get(`/transaction-reports/preview/${setupNo}/${docNo}`)
        ).data;

        if (!response.data) {
            throw new Error("Report preview data not found");
        }

        return response.data;
    } catch (error: any) {
        if (error?.response?.data?.detail) {
            throw new Error(error.response.data.detail);
        }
        throw new Error("Failed to fetch report preview");
    }
};
